// apps/cli/src/lib/bootstrap-aci.ts
import { runAz } from "./az.js";

export interface GitHubAppCreds {
  appId: string;
  slug: string;
  org: string;
  installationId: string;
  /** base64-encoded PEM — treated as a secret, passed as a secure env var. */
  pemB64: string;
}

export interface AciSpec {
  aciName: string;
  resourceGroup: string;
  location: string;
  subscriptionId: string;
  miResourceId: string;
  miClientId: string;
  appRegClientId: string;
  image: string;
  gatewayImageRef?: string;
  foundryTracing?: string;
  githubApp?: GitHubAppCreds;
}

/**
 * Build the `az container create` args (MI attach + the F3 RETRO §1 runtime env).
 *
 * Uses `--restart-policy OnFailure` so a transient first-command death (e.g. MI
 * propagation lag before `login_with_retry` was introduced) triggers a container
 * restart and self-heals. The entrypoint is idempotent (hydrate_status restores
 * prior progress on restart). `fail()` exits 0 after writing the `failed` status
 * blob — the blob is the source of truth, so an honest failure stops ACI from
 * restart-cycling indefinitely.
 */
export function buildAciCreateArgs(s: AciSpec): string[] {
  // Plain (non-secret) environment variables — one --environment-variables block.
  const env = [
    `SUBSCRIPTION_ID=${s.subscriptionId}`,
    `RESOURCE_GROUP=${s.resourceGroup}`,
    `LOCATION=${s.location}`,
    `MI_CLIENT_ID=${s.miClientId}`,
    `APP_REG_CLIENT_ID=${s.appRegClientId}`,
  ];
  if (s.gatewayImageRef) env.push(`GATEWAY_IMAGE_REF=${s.gatewayImageRef}`);
  if (s.foundryTracing) env.push(`FOUNDRY_TRACING=${s.foundryTracing}`);
  if (s.githubApp) {
    // Non-secret GitHub App metadata is safe in the plain env block.
    const g = s.githubApp;
    env.push(
      `GITHUB_ORG=${g.org}`,
      `GH_APP_ID=${g.appId}`,
      `GH_APP_SLUG=${g.slug}`,
      `GH_INSTALLATION_ID=${g.installationId}`,
    );
  }

  const args = [
    "container", "create",
    "--name", s.aciName,
    "--resource-group", s.resourceGroup,
    "--location", s.location,
    "--subscription", s.subscriptionId,
    "--image", s.image,
    "--os-type", "Linux",
    "--restart-policy", "OnFailure",
    "--assign-identity", s.miResourceId,
    "--cpu", "1", "--memory", "1.5",
    "--environment-variables", ...env,
  ];

  if (s.githubApp) {
    // The PEM is a secret — pass it via --secure-environment-variables so az
    // CLI redacts it from logs and the ACI properties API response.
    args.push("--secure-environment-variables", `GH_APP_PEM_B64=${s.githubApp.pemB64}`);
  }

  args.push("--only-show-errors");
  return args;
}

export async function kickInstaller(s: AciSpec): Promise<void> {
  await runAz(buildAciCreateArgs(s));
}
